export const sections = ["bloodTests", "fluids", "medications", "imaging"] as const;
export type Section = typeof sections[number];
export type Evidence = { fileId: string; page: number; quote: string; supports: string[] };
export type Quality = "documented" | "uncertain" | "conflicting";
export type Common = { id: string; evidence: Evidence[]; quality: Quality; notes: string; correlation: string; issues: string[] };
export type BloodTest = Common & {
  test: string; requestedAt: string | null; collectedAt: string | null; resultedAt: string | null;
  specimenId: string | null; specimen: string | null;
  resultsSheetStatus: "matched" | "not_found" | "ambiguous" | "not_checked";
  status: "requested" | "collected" | "resulted" | "cancelled" | "mentioned" | "unclear";
  results: string; indication: string; count: number | null;
  specimenType: "blood" | "csf" | "urine";
  componentAnalytes: Array<{analyte:string;value:string;unit:string}>;
};
export type ResultSheetEntry = {test:string;specimenType:"blood"|"csf"|"urine";specimenId:string|null;collectionDate:string|null;evidence:Evidence[]};
export type Fluid = Common & {
  fluid: string; additives: string; route: string; line: string | null;
  start: string | null; end: string | null; date: string | null;
  rateMlHr: number | null; volumeMl: number | null;
  status: "administered" | "prescribed" | "mentioned" | "unclear";
  entryKind: "interval" | "bolus" | "chart_total";
  category: "iv_infusion" | "iv_bolus" | "enteral_feed" | "fluid_target";
  volumePerFeedMl: number | null; dailySubtotalMl: number | null;
  frequencyHours: number | null; feedsGiven: number | null;
  durationHours?: number | null; calculatedMl?: number | null; projectedMl24h?: number | null;
  includedMl?: number | null; calculation?: string;
};
export type Medication = Common & {
  medication: string; category: "antibiotic" | "other";
  start: string | null; end: string | null; dose: string; route: string; frequency: string;
  indication: string; administrations: string; administeredDoses: number | null;
  standardizedName: "ampicillin" | "gentamicin" | "sodium_bicarbonate" | "other";
  administrationEvents: Array<{date:string;time:string;status:"administered"|"omitted"|"withheld";signerInitials:string|null}>;
  intendedDurationDays: number | null;
  status: "administered" | "prescribed" | "withheld" | "stopped" | "mentioned" | "unclear";
};
export type Imaging = Common & {
  modality:"CXR"|"Echocardiogram"|"Cranial_Ultrasound"|"aEEG"|"Hearing_AABR"|"Other";
  status:"performed"|"requested"|"scheduled";
  studyDate:string|null; repeatScheduledDate:string|null; findingsOrIndication:string;
};
export type DailyFluid = { date: string; documentedMl: number; calculatedMl: number; enteralMl:number; knownTotalMl: number; excludedEntries: number; note: string };
export type Identity = { status: "single" | "uncertain" | "multiple"; patient: string; episode: string; reason: string; evidence: Evidence[]; hospitalNumbers:string[]; dateOfBirth:string|null; birthWeightG:number|null; gestationalAgeWeeks:number|null };
export type Result = {
  schemaVersion: 3; model: string; generatedAt: string; identity: Identity;
  bloodTests: BloodTest[]; fluids: Fluid[]; medications: Medication[]; imaging:Imaging[]; dailyFluids: DailyFluid[];
  resultSheetEntries:ResultSheetEntry[]; warnings: string[]; sectionStatus: Record<Section, "complete" | "partial" | "failed">;
};
export type Source = { id: string; name: string; pages: number; size: number; hash: string };
export type Job = {
  id: string; accessHash: string; status: "queued" | "processing" | "ready" | "failed" | "cancelled";
  stage: string; progress: number; createdAt: string; expiresAt: string; sources: Source[];
  result?: Result; error?: string; warnings: string[]; remoteFiles: string[];
};
export type PublicJob = Omit<Job, "accessHash" | "remoteFiles">;
export const labels: Record<Section, string> = { bloodTests: "Pathology & microbiology", fluids: "Fluids & nutrition", medications: "Medications", imaging:"Imaging & monitoring" };
